import {computed, Injector, signal, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {BehaviorSubject, EMPTY, Observable, of, Subject} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {DexStreamingInitResolverService} from '../../components/dex/dex-streaming-init-resolver.service';
import {DexComputedTrancheService} from '../../components/dex/dex-streaming/computed/dex-computed-tranche.service';
import {ComputedTranche} from '../../components/dex/dex-streaming/types/computed/computed-tranche';
import {DexTrancheUtilityService} from '../../components/dex/dex-tranche-utility/dex-tranche-utility.service';
import {WIDGET_DESCRIPTION} from '../../components/layout/widget/widget-description';
import {
    WidgetImplementation,
    WidgetImplementationEvent,
    WidgetImplementationEventFlags
} from '../../components/layout/widget/widget-implementation';
import {
    createWHeaderTitlePartText,
    WidgetHeaderTitlePart
} from '../../pages/dashboard/layout/widget/widget-header/widget-header-title';
import {
    createWHeaderTitlePartGroupShared
} from '../../pages/dashboard/layout/widget/widget-header/widget-header-title/parts/w-header-title-part-group-shared/w-header-title-part-group-shared';
import {
    getSettingsWithUpdateField,
    getWidgetSettingsWithUpdatesStream,
    hasSettingUpdateField,
    pipeSettingsCorrections,
    setWidgetSettings,
    updateWidgetSettings
} from '../components/common-widget.implementation';
import {DexTrancheDetailsFeature, DexTrancheDetailsFeatures} from './settings/dex-lp-details-feature';
import {DexTrancheDetailsWidgetSettings} from './settings/dex-tranche-details-widget-settings';
import {DexTrancheDetailsWidgetSettingsService} from './settings/dex-tranche-details-widget-settings.service';

export class DexTrancheDetailsWidgetImplementation implements WidgetImplementation {

    public settings: DexTrancheDetailsWidgetSettings;

    public settingsService: DexTrancheDetailsWidgetSettingsService = this.injector.get(DexTrancheDetailsWidgetSettingsService);

    public ready$ = new BehaviorSubject<boolean>(false);

    public event$ = new Subject<WidgetImplementationEvent>();

    public readonly $features = signal<DexTrancheDetailsFeatures>({});

    public settingsWithUpdates$: Observable<Partial<DexTrancheDetailsWidgetSettings>> =
               getWidgetSettingsWithUpdatesStream(this);

    public readonly titleParts$: Observable<WidgetHeaderTitlePart[]>;

    public readonly initialDependency$ = this.injector.get(DexStreamingInitResolverService).resolve();

    public readonly $tranche: Signal<ComputedTranche>;

    private readonly dexComputedTrancheService = this.injector.get(DexComputedTrancheService);
    private readonly dexTrancheUtilityService = this.injector.get(DexTrancheUtilityService);

    constructor(
        private injector: Injector
    ) {
        this.$tranche = this.createTrancheSignal();
        this.titleParts$ = this.getTitlePartsStream();
    }

    public setSettings(
        settings?: DexTrancheDetailsWidgetSettings,
        flags?: WidgetImplementationEventFlags
    ): Observable<DexTrancheDetailsWidgetSettings> {
        return setWidgetSettings(this, settings, flags);
    }

    public updateSettings(
        updates?: Partial<DexTrancheDetailsWidgetSettings>,
        flags?: WidgetImplementationEventFlags
    ): Observable<Partial<DexTrancheDetailsWidgetSettings>> {
        return updateWidgetSettings(this, updates, flags);
    }

    public resolveSettings(
        settings: Partial<DexTrancheDetailsWidgetSettings>
    ): Observable<Partial<DexTrancheDetailsWidgetSettings>> {
        return pipeSettingsCorrections<DexTrancheDetailsWidgetSettings>([
            s => this.resolveSettingsFeatures(s),
            s => this.dexTrancheUtilityService.getTrancheSymbolFix(s, this.settings)
        ])(settings, this);
    }

    public toggleFeatureCollapsed(newState?: boolean): Observable<Partial<DexTrancheDetailsWidgetSettings>> {
        const oldState = this.$features().COLLAPSED;
        newState = newState === undefined ? !oldState : newState;

        let result$: Observable<Partial<DexTrancheDetailsWidgetSettings>> = EMPTY;

        if (newState !== oldState) {
            const newSettingsFeatures: DexTrancheDetailsFeature[] = oldState
                ? this.settings.features.filter(f => f !== DexTrancheDetailsFeature.COLLAPSED)
                : [...this.settings.features, DexTrancheDetailsFeature.COLLAPSED];

            result$ = this.updateSettings({
                features: newSettingsFeatures
            });
        }

        return result$;
    }

    private readSettingsFeatures(
        settings?: Partial<DexTrancheDetailsWidgetSettings>
    ): void {
        if (settings?.features) {
            this.$features.set(
                Object.fromEntries(settings.features.map(key => [key, true]))
            );
        }
    }

    private resolveSettingsFeatures(
        settingsUpdate: Partial<DexTrancheDetailsWidgetSettings>
    ): Observable<Partial<DexTrancheDetailsWidgetSettings>> {
        if (hasSettingUpdateField(settingsUpdate, 'features')) {
            this.readSettingsFeatures(settingsUpdate);
        }

        return of(undefined);
    }

    private getTitlePartsStream(): Observable<WidgetHeaderTitlePart[]> {
        const {widgetLabel} = this.injector.get(WIDGET_DESCRIPTION);

        const getTitleParts = (): WidgetHeaderTitlePart[] => {
            const {title: settingsTitle, symbol} = this.settings || {} as DexTrancheDetailsWidgetSettings;

            const commonPart = createWHeaderTitlePartText(
                settingsTitle || widgetLabel,
                !settingsTitle
            );

            const titleParts: WidgetHeaderTitlePart[] = [commonPart];

            if (symbol) {
                titleParts.push(createWHeaderTitlePartGroupShared(symbol, 'trancheSymbol', symbol));
            }

            return titleParts;
        };

        return this.settingsWithUpdates$.pipe(
            filter(settingsUpdate =>
                hasSettingUpdateField(settingsUpdate, 'title')
                || hasSettingUpdateField(settingsUpdate, 'symbol')
            ),
            map(getTitleParts)
        );
    }

    private createTrancheSignal(): Signal<ComputedTranche> {
        const symbol$ = this.settingsWithUpdates$.pipe(
            filter(update => hasSettingUpdateField(update, 'symbol')),
            map(update => getSettingsWithUpdateField(this.settings, update, 'symbol'))
        );

        const $symbol = toSignal(symbol$);

        return computed(() => {
            const symbol = $symbol();

            return this.dexComputedTrancheService.$list()
                .find(tranche => tranche.unitDeliverable.symbol === symbol);
        });
    }

}
